import React from 'react';
import styled, { keyframes } from 'styled-components';
import {
    COLORS,
    sp,
    OrderViewContent,
    OrderViewColumns,
    OrderViewSection,
    OrderViewSectionTitle,
    OrderViewLineItems,
    OrderViewDataCardStack,
} from '../styles';
import { SELECTABLE_ITEM_COLOR } from '../design';
import { OrderConfirmationLayout } from '../types';
import Divider from './Divider';
import Spinner from './Spinner';

const shimmer = keyframes`
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
`;

const Block = styled.div<{ $w?: string; $h?: string; $radius?: string }>`
    inline-size: ${({ $w }) => $w ?? '100%'};
    block-size: ${({ $h }) => $h ?? '14px'};
    border-radius: ${({ $radius }) => $radius ?? '6px'};
    background-color: ${COLORS.mutedFill};
    animation: ${shimmer} 1.2s ease-in-out infinite;
    flex-shrink: 0;
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLORS.icon};
    pointer-events: none;
`;

// Borrows the section title's sp(4.5) gap to the body.
const SkeletonTitle = styled(OrderViewSectionTitle).attrs({ as: 'div' })``;

// Mirrors LineItem's Ctn: image + infos + price column, gap sp(2.5).
const SkeletonLineItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${sp(2.5)};
`;

const SkeletonLineItemInfos = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(1)};
    flex-grow: 1;
    min-width: 0;
`;

const SkeletonLineItemRight = styled.div`
    display: flex;
    align-items: center;
    gap: 1em;
    flex-shrink: 0;
`;

// Mirrors PurchaseSummary's Wrapper: stacked space-between lines, gap sp(2).
const SkeletonSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(2)};
`;

const SkeletonSummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${sp(2)};
`;

// Mirrors DataCard's Card: padded, bordered, icon slot + body, gap sp(2).
const SkeletonCard = styled.div`
    display: flex;
    align-items: center;
    padding: ${sp(2)};
    gap: ${sp(2)};
    border-radius: 8px;
    border: 1px solid ${COLORS.border};
`;

const SkeletonCardBody = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: ${sp(0.5)};
`;

const SkeletonLineItemsSection: React.FC = () => (
    <OrderViewSection>
        <SkeletonTitle>
            <Block $w='40%' $h='24px' />
        </SkeletonTitle>
        <OrderViewLineItems>
            <SkeletonLineItem>
                <Block $w='110px' $h='110px' $radius='8px' />
                <SkeletonLineItemInfos>
                    <Block $w='60%' $h='16px' />
                    <Block $w='35%' $h='14px' />
                    <Block $w='30%' $h='14px' />
                </SkeletonLineItemInfos>
                <SkeletonLineItemRight>
                    <Block $w='24px' $h='16px' />
                    <Block $w='60px' $h='16px' />
                </SkeletonLineItemRight>
            </SkeletonLineItem>
            <Divider />
            <SkeletonLineItem>
                <Block $w='110px' $h='110px' $radius='8px' />
                <SkeletonLineItemInfos>
                    <Block $w='55%' $h='16px' />
                    <Block $w='30%' $h='14px' />
                </SkeletonLineItemInfos>
                <SkeletonLineItemRight>
                    <Block $w='24px' $h='16px' />
                    <Block $w='60px' $h='16px' />
                </SkeletonLineItemRight>
            </SkeletonLineItem>
        </OrderViewLineItems>
        <SkeletonSummary>
            <SkeletonSummaryRow>
                <Block $w='30%' $h='16px' />
                <Block $w='15%' $h='16px' />
            </SkeletonSummaryRow>
            <SkeletonSummaryRow>
                <Block $w='25%' $h='16px' />
                <Block $w='15%' $h='16px' />
            </SkeletonSummaryRow>
            <Divider />
            <SkeletonSummaryRow>
                <Block $w='20%' $h='18px' />
                <Block $w='20%' $h='18px' />
            </SkeletonSummaryRow>
        </SkeletonSummary>
    </OrderViewSection>
);

interface SkeletonDataCardProps {
    /** Number of body lines under the (optional) first label line. */
    lines?: number;
}

const SkeletonDataCard: React.FC<SkeletonDataCardProps> = ({ lines = 1 }) => (
    <SkeletonCard>
        <Block $w='22px' $h='22px' $radius='50%' />
        <SkeletonCardBody>
            {Array.from({ length: lines }).map((_, i) => (
                <Block
                    // Static skeleton list — index keys are fine here.
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    $w={i === 0 ? '45%' : `${75 - i * 10}%`}
                    $h='14px'
                />
            ))}
        </SkeletonCardBody>
    </SkeletonCard>
);

const SkeletonContactSection: React.FC = () => (
    <OrderViewSection>
        <SkeletonTitle>
            <Block $w='40%' $h='24px' />
        </SkeletonTitle>
        <SkeletonDataCard lines={1} />
    </OrderViewSection>
);

const SkeletonShippingSection: React.FC = () => (
    <OrderViewSection>
        <SkeletonTitle>
            <Block $w='40%' $h='24px' />
        </SkeletonTitle>
        <OrderViewDataCardStack>
            <SkeletonDataCard lines={2} />
            <SkeletonDataCard lines={2} />
        </OrderViewDataCardStack>
    </OrderViewSection>
);

const SkeletonPaymentSection: React.FC = () => (
    <OrderViewSection>
        <SkeletonTitle>
            <Block $w='40%' $h='24px' />
        </SkeletonTitle>
        <OrderViewDataCardStack>
            <SkeletonDataCard lines={2} />
            <SkeletonDataCard lines={2} />
        </OrderViewDataCardStack>
    </OrderViewSection>
);

interface LoadingStateProps {
    layout?: OrderConfirmationLayout;
    // Both keep the skeleton's borders and spinner from snapping when the order
    // resolves.
    dividerColor?: string;
    primaryTextColor?: string;
}

const LoadingState: React.FC<LoadingStateProps> = ({
    layout = 'ONE_COLUMN',
    dividerColor = SELECTABLE_ITEM_COLOR,
    primaryTextColor = SELECTABLE_ITEM_COLOR,
}) => {
    const isTwoColumns = layout === 'TWO_COLUMNS';

    return (
        <OrderViewContent
            data-auto='ssr-order-confirmation-widget'
            style={
                {
                    position: 'relative',
                    minHeight: 600,
                    '--ocw-border-color': dividerColor,
                    '--ocw-primary-text-color': primaryTextColor,
                } as React.CSSProperties
            }
        >
            <SkeletonLineItemsSection />
            <Divider />
            <SkeletonContactSection />
            {isTwoColumns ? (
                <>
                    <Divider />
                    <OrderViewColumns data-auto='ssr-order-confirmation-columns'>
                        <SkeletonShippingSection />
                        <SkeletonPaymentSection />
                    </OrderViewColumns>
                </>
            ) : (
                <>
                    <Divider />
                    <SkeletonShippingSection />
                    <Divider />
                    <SkeletonPaymentSection />
                </>
            )}

            <SpinnerOverlay>
                <Spinner size={32} thickness={3} />
            </SpinnerOverlay>
        </OrderViewContent>
    );
};

export default LoadingState;
